import { type fieldSchemaDefV1 } from '../../../common/fieldSchemaDef';
import { FieldWizard } from './FieldWizard';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ActionIcon, Anchor, Menu, Table } from '@mantine/core';
import { modals } from '@mantine/modals';
import { AlignJustify, Ellipsis, Trash2 } from 'lucide-react';
import { type CSSProperties } from 'react';
import { ulid } from 'ulid';
import { Link } from 'wouter';
import { type z } from 'zod';

const RowDragHandleCell = ({ rowId }: { readonly rowId: string }) => {
  const { attributes, listeners } = useSortable({
    id: rowId,
  });
  return (
    <AlignJustify
      className="cursor-grab outline-0 align-middle leading-none"
      size={20}
      {...attributes}
      {...listeners}
    />
  );
};

export const DraggableRow = ({
  deleteHandler,
  fieldDef,
  updateHandler,
}: {
  readonly deleteHandler: (fieldId: string) => void;
  readonly fieldDef: z.infer<typeof fieldSchemaDefV1.shape.fields.element>;
  readonly updateHandler: (updatedFieldDef: typeof fieldDef) => void;
}) => {
  const { isDragging, setNodeRef, transform, transition } = useSortable({
    id: fieldDef.id,
  });
  const editModalId = ulid();

  const style: CSSProperties = {
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
  };
  return (
    <Table.Tr
      key={fieldDef.name}
      ref={setNodeRef}
      style={style}
    >
      <Table.Td py={0}>
        <RowDragHandleCell rowId={fieldDef.id} />
      </Table.Td>
      <Table.Td
        className="text-nowrap overflow-ellipsis overflow-x-hidden max-w-svh"
        title={fieldDef.name}
        width="60%"
      >
        {fieldDef.name}
      </Table.Td>
      <Table.Td width="40%">{fieldDef.type}</Table.Td>
      <Table.Td
        className="text-nowrap"
        py={0}
      >
        <Anchor
          component={Link}
          // to={`/types/${id}/fields/${fieldDef.name}`}
          onClick={() =>
            modals.open({
              children: (
                <FieldWizard
                  field={fieldDef}
                  onSubmit={(data) => {
                    updateHandler(data);
                    modals.close(editModalId);
                  }}
                />
              ),
              modalId: editModalId,
              size: 'lg',
            })
          }
        >
          Edit
        </Anchor>
      </Table.Td>
      <Table.Td py={0}>
        <Menu>
          <Menu.Target>
            <ActionIcon
              aria-label="Settings"
              className="align-middle"
              size="sm"
              variant="subtle"
            >
              <Ellipsis />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              color="red"
              leftSection={<Trash2 />}
              onClick={() => deleteHandler(fieldDef.id)}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
    </Table.Tr>
  );
};
